import { inject, enableProdMode, provideAppInitializer, importProvidersFrom } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, Routes } from '@angular/router';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { RouteAccessGuard } from '@security/route-access.guard';
import { environment } from './environments/environment';
import { AppComponent } from './app/app.component';
import { registerLocaleData } from '@angular/common';
import localeNl from '@angular/common/locales/nl';
import { NgxHateoasClientConfigurationService } from '@lagoshny/ngx-hateoas-client';
import { Settings } from '@shared/models/settings/settings';
import { EnabledAuthMethod } from '@shared/models/settings/enabled-auth-method';
import { RoleAuthorization } from '@shared/models/settings/role-authorisation';
import {AutoRefreshTokenService, createInterceptorCondition, INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG, IncludeBearerTokenCondition, includeBearerTokenInterceptor, provideKeycloak, UserActivityService} from 'keycloak-angular';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader, TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { canActivateAuthRole } from '@security/app-auth.guard';
import {initializeApp} from '@utils/app-init';

if (environment.production) {
    enableProdMode();
}

registerLocaleData(localeNl, 'nl');

const provideHateoasInit = provideAppInitializer(() => {
    const hateoas = inject(NgxHateoasClientConfigurationService);
    hateoas.configure({
        http: { rootUrl: environment.springAppUrl },
        useTypes: {
            resources: [Settings],
            embeddedResources: [EnabledAuthMethod, RoleAuthorization],
        },
    });
});
//
// const provideKeycloakInit = {
//     provide: APP_INITIALIZER,
//     useFactory: (keycloak: KeycloakService, translator: TranslatorService, config: ConfigService, errorDisplay: ErrorDisplayService) => {
//         return initializer(keycloak, translator, config, errorDisplay, window.location);
//     },
//     multi: true,
//     deps: [KeycloakService, TranslatorService, ConfigService, ErrorDisplayService]
// };

export function HttpLoaderFactory() {
    return new TranslateHttpLoader();
}

const routes: Routes = [
    {
        path: '',
        canActivate: [canActivateAuthRole],
        canActivateChild: [RouteAccessGuard],
        children: [
            {
                path: '',
                redirectTo: 'sign-request',
                pathMatch: 'full'
            },
            {
                path: 'sign-request',
                loadChildren: () => import('./app/routes/sign-request/sign-request.module').then(m => m.SignRequestModule)
            },
            {
                path: 'dashboard',
                loadChildren: () => import('./app/routes/dashboard/dashboard.module').then(m => m.DashboardModule)
            },
            {
                path: 'administration',
                loadChildren: () => import('./app/routes/administration/administration.module').then(m => m.AdministrationModule)
            },
            {
                path: 'edelivery',
                loadChildren: () => import('./app/routes/edelivery/edelivery.module').then(m => m.EdeliveryModule)
            },
            {
                path: 'contacts',
                loadChildren: () => import('./app/routes/contacts/contacts.module').then(m => m.ContactsModule)
            },
            {
                path: 'workflow',
                loadChildren: () => import('./app/routes/workflow/workflow.module').then(m => m.WorkflowModule)
            },
            {
                path: 'users',
                loadChildren: () => import('./app/routes/users/users.module').then(m => m.UsersModule)
            },
            {
                path: 'api-accounts',
                loadChildren: () => import('./app/routes/api-accounts/api-accounts.module').then(m => m.ApiAccountsModule)
            },
            {
                path: 'profile',
                loadChildren: () => import('./app/routes/profile/profile.module').then(m => m.ProfileModule)
            },
            {
                path: 'sysops',
                loadChildren: () => import('@routes/sysops/sys-ops.module').then(m => m.SysOpsModule)
            },
            {
                path: 'templates',
                loadChildren: () => import('@routes/templates/templates.module').then(m => m.TemplatesModule)
            },
            {
                path: 'eseal',
                loadChildren: () => import('@routes/eseal/eseal.module').then(m => m.EsealModule)
            },
            // Not found
            { path: '**', redirectTo: 'documents' }
        ]
    }
];

export function getAccountName(location: Location): string {
    if (location.hostname === 'localhost') {
        return 'zynyo.test';
    } else {
        const subdomain = location.hostname.split('.dtm.zynyo.com').find(path => path);
        return subdomain ?? 'kubernetes';
    }
}

async function startApp() {
    let keycloakConfig = environment.keycloakConfig;
    try {
        const account = getAccountName(window.location);
        const res = await fetch(`${environment.dtmConfigUrl}/${account}`);
        if (res.ok) {
            const config = await res.json();
            if (config.keycloakConfig) {
                keycloakConfig = config.keycloakConfig;
            }
        }
    } catch (e) {
        console.warn('Failed to fetch dynamic config before bootstrap, falling back to environment', e);
    }

    bootstrapApplication(AppComponent, {
        providers: [
            importProvidersFrom(ModalModule.forRoot()),
            importProvidersFrom(TranslateModule.forRoot()),
            provideTranslateHttpLoader({
                useHttpBackend: true
            }),
            provideAnimationsAsync(),
            provideRouter(routes),                    // your routes here
            provideHttpClient(withInterceptors([includeBearerTokenInterceptor]), withInterceptorsFromDi()),
            provideHateoasInit,
            provideKeycloak({
                config: keycloakConfig,
                initOptions: {
                    onLoad: 'check-sso',
                    silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
                },
                providers: [
                    AutoRefreshTokenService,
                    UserActivityService,
                    {
                        provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
                        useValue: [
                            createInterceptorCondition<IncludeBearerTokenCondition>({
                                urlPattern: /^(?!.*assets).*/,
                            }),
                        ]
                    }
                ],
            }),
            provideAppInitializer(() => initializeApp()),
        ],
    }).catch(err => console.error(err));
}

startApp();
